'use strict'

import React, { useCallback, useLayoutEffect, useRef, useState } from 'react'
import Icon from '@mdi/react'
import { mdiFile } from '@mdi/js'

import Box from '@components/kit/box'
import Text from '@components/kit/text'
import Pressable from '@components/kit/pressable'
import { CloseIcon } from '@components/kit/icons'
import { useNavigate } from 'react-router-dom'
import { colors } from '@theme'

import { useChatStore } from '@store/Chat'
import { IncomingMessageNotification } from '@store/Chat/types'
import {
  ManagedToast,
  ToastPhase,
  useManagedToasts
} from './use-managed-toasts'
import { useToastFlip } from './use-toast-flip'
import './incoming-message-toast.css'

const NAV_WIDTH = 98

type ToastItemProps = {
  notification: IncomingMessageNotification
  phase: ToastPhase
  itemRef: (element: HTMLDivElement | null) => void
  onOpen: (notification: IncomingMessageNotification) => void
  onDismiss: (id: string) => void
}

function ToastItem({
  notification,
  phase,
  itemRef,
  onOpen,
  onDismiss
}: ToastItemProps) {
  const [isCollapsed, setIsCollapsed] = useState(false)
  const localRef = useRef<HTMLDivElement | null>(null)

  useLayoutEffect(() => {
    if (phase !== 'exiting') {
      setIsCollapsed(false)
      return
    }

    const element = localRef.current
    if (!element) {
      return
    }

    element.style.setProperty(
      '--toast-height',
      `${element.getBoundingClientRect().height}px`
    )

    const frame = requestAnimationFrame(() => {
      setIsCollapsed(true)
    })

    return () => cancelAnimationFrame(frame)
  }, [phase])

  const phaseClass =
    phase === 'entering'
      ? 'toast-item--entering'
      : phase === 'exiting'
      ? 'toast-item--exiting'
      : ''

  const collapsedClass =
    phase === 'exiting' && isCollapsed ? 'toast-item--collapsed' : ''

  const handleDismiss = (event: { stopPropagation?: () => void }) => {
    event.stopPropagation?.()
    onDismiss(notification.id)
  }

  const setRef = (element: HTMLDivElement | null) => {
    localRef.current = element
    itemRef(element)
  }

  return (
    <div
      ref={setRef}
      className={`toast-item ${phaseClass} ${collapsedClass}`.trim()}
    >
      <Pressable onPress={() => onOpen(notification)}>
        <div className='toast-box'>
          <Box flexDirection='row' alignItems='flex-start'>
            <Box flex='1 1 auto' pr='08'>
              <Text font='text.14.bold' textColor='purple' mb='04'>
                New message from {notification.name}
              </Text>
              <Box flexDirection='row' alignItems='center'>
                {notification.isFilePreview && (
                  <Box mr='04' style={{ flexShrink: 0 }}>
                    <Icon
                      path={mdiFile}
                      size='14px'
                      color={
                        (colors as Record<string, string>).shade06 ?? '#4B5563'
                      }
                    />
                  </Box>
                )}
                <Text font='text.14.medium' numberOfLines={2} flex='1 1 auto'>
                  {notification.preview}
                </Text>
              </Box>
            </Box>
            <Pressable
              onPress={handleDismiss}
              alignItems='center'
              justifyContent='center'
              style={{ width: 20, height: 20 }}
            >
              <CloseIcon width={15} height={15} fill='shade05' />
            </Pressable>
          </Box>
        </div>
      </Pressable>
    </div>
  )
}

function IncomingMessageToast() {
  const navigate = useNavigate()
  const itemRefs = useRef<Map<string, HTMLElement>>(new Map())
  const notifications = useChatStore(
    state => state.incomingMessageNotifications
  )
  const handleRemove = useCallback((id: string) => {
    useChatStore.getState().removeIncomingMessageNotification(id)
  }, [])

  const { toasts, dismissToast } = useManagedToasts(notifications, handleRemove)

  useToastFlip(toasts, itemRefs)

  const setItemRef = useCallback(
    (id: string, element: HTMLDivElement | null) => {
      if (element) {
        itemRefs.current.set(id, element)
        return
      }

      itemRefs.current.delete(id)
    },
    []
  )

  const handleOpenChat = (notification: IncomingMessageNotification) => {
    dismissToast(notification.id)
    navigate(`/chat/${notification.userId}`)
  }

  if (toasts.length === 0) {
    itemRefs.current.clear()
    return null
  }

  return (
    <Box
      position='fixed'
      bottom={24}
      left={{ tablet: NAV_WIDTH + 24, mobile: 24 }}
      zIndex={9999}
      maxWidth={{ tablet: 360, mobile: '75%' }}
      width='100%'
    >
      <div className='toast-stack'>
        {toasts.map((toast: ManagedToast) => (
          <ToastItem
            key={toast.notification.id}
            notification={toast.notification}
            phase={toast.phase}
            itemRef={element => setItemRef(toast.notification.id, element)}
            onOpen={handleOpenChat}
            onDismiss={dismissToast}
          />
        ))}
      </div>
    </Box>
  )
}

export default IncomingMessageToast
